import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import AccountRoles from '../../account/enums/AccountRoles';
import PaymentModels from '../../account/enums/PaymentModels';
import DateInput from '../../common/component/form/DateInput';
import Dialog from '../../common/component/form/Dialog';
import RadioSelection from '../../common/component/form/RadioSelection';
import TextInput from '../../common/component/form/TextInput';
import ErrorCode from '../../common/enums/ErrorCode';
import {convertDateToIsoDateString} from '../../common/util/DateUtil';
import {checkValidation} from '../../common/util/ValidationUtil';
import clubApi from '../api/clubApi';

export default function ClubDialog({
                                       open,
                                       onSave,
                                       onCancel,
                                       club: initialClub
                                   }) {
    const [t] = useTranslation();

    const role = useSelector(state => state.account.role);
    const currentLanguage = useSelector(state => state.i18n.currentLanguage);
    const [club, setClub] = useState(null);
    const [validation, setValidation] = useState();
    const [isValidated, setIsValidated] = useState(false);
    const [clubAlreadyExistsError, setClubAlreadyExistsError] = useState(false);

    useEffect(() => {
        if (open) {
            setClub(initialClub ? {
                ...initialClub,
                paidModelEndDate: initialClub.paidModelEndDate ? new Date(initialClub.paidModelEndDate) : null
            } : {
                serialNumber: null,
                name: null,
                paymentModel: PaymentModels.FREE,
                paidModelEndDate: null
            });
            setClubAlreadyExistsError(false);
        }
    }, [open]);

    const handleChange = (key, value) => {
        const updatedClub = {...club};
        updatedClub[key] = value;
        setClub(updatedClub);
    };

    const handleSave = () => {
        if (validate()) {
            const clubToSend = {
                ...club,
                paidModelEndDate: club.paidModelEndDate ? convertDateToIsoDateString(club.paidModelEndDate) : null
            };

            clubApi.createOrUpdateClub(clubToSend).then(response => {
                if (response.data.success) {
                    setClubAlreadyExistsError(false);
                    onSave(response.data.result);
                } else {
                    if (response.errorCode === ErrorCode.DATA_ALREADY_EXISTS.code) {
                        setClubAlreadyExistsError(true);
                    }
                }
            });
        }
    };

    const validate = () => {
        const newValidation = {
            // Non-admins can't update the serial number -> not required in that case
            serialNumber: role !== AccountRoles.ADMINISTRATOR || !!club?.serialNumber
        };
        setIsValidated(true);
        setValidation(newValidation);
        return checkValidation(newValidation);
    };

    if (!club) {
        return null;
    }

    return (
        <Dialog
            open={open}
            title={club.id ? t('Club.EDIT') : t('Club.ADD')}
            footer={
                <>
                    <button
                        onClick={onCancel}
                        type="button"
                        className="btn btn-transparent-primary">
                        <FontAwesomeIcon icon="times" className="mr-2"/>
                        {t('Button.CANCEL')}
                    </button>

                    <button
                        onClick={handleSave}
                        type="button"
                        className="btn btn-primary">
                        <FontAwesomeIcon icon="check" className="mr-2"/>
                        {t('Button.SAVE')}
                    </button>
                </>
            }
        >
            {
                role === AccountRoles.ADMINISTRATOR &&
                <TextInput
                    label={`${t('Club.SERIAL_NUMBER')}*`}
                    value={club.serialNumber}
                    onChange={value => handleChange('serialNumber', value)}
                    required={clubAlreadyExistsError || isValidated && !validation.serialNumber}
                    errorMessage={clubAlreadyExistsError ? t('Error.CLUB_ALREADY_EXISTS') : null}
                />
            }
            <TextInput
                label={t('Club.NAME')}
                value={club.name}
                onChange={value => handleChange('name', value)}
            />
            {
                role === AccountRoles.ADMINISTRATOR &&
                <>
                    <div className="form-container">
                        <h6>{t('Club.PAYMENT_MODEL')}*</h6>
                        <RadioSelection
                            options={Object.values(PaymentModels).map((val) => {
                                if (PaymentModels.hasOwnProperty(val)) {
                                    return {
                                        value: val,
                                        label: t('Club.PAYMENT_MODEL_' + val)
                                    };
                                }
                            })}
                            selected={club.paymentModel ? club.paymentModel : PaymentModels.FREE}
                            onChange={value => handleChange('paymentModel', value)}
                            required={false}
                        />
                    </div>
                    {
                        club.paymentModel === PaymentModels.PAID &&
                        <div className="form-container">
                            <h6>{t('Club.END_OF_PAID_MODEL')}</h6>
                            <DateInput
                                selected={club.paidModelEndDate ? new Date(club.paidModelEndDate) : null}
                                onSelect={value => handleChange('paidModelEndDate', value)}
                                label={t('Training.DATE')}
                                value={club.paidModelEndDate ? new Date(club.paidModelEndDate) : null}
                                onChange={value => handleChange('paidModelEndDate', value)}
                                locale={currentLanguage}
                                minDate={new Date(Date.now())}
                                dateFormat="dd.MM.yyyy"
                                isClearable={true}
                            />
                        </div>
                    }
                </>
            }
        </Dialog>
    );
}
